import { SPELL_COPY } from "./spells";
import type { AppState, Player } from "./types";

function editablePlayer(state: AppState): Player | undefined {
  if (
    state.screen !== "match" ||
    state.snapshot?.phase !== "shop" ||
    !state.connected ||
    state.reconnecting ||
    state.menuOpen ||
    state.pendingRequest != null ||
    state.pendingReorder
  )
    return undefined;
  const player = state.snapshot.players.find(
    (p) => p.id === state.matchPlayerId,
  );
  return player && !player.ready ? player : undefined;
}

export function canReorderSpells(state: AppState): boolean {
  return editablePlayer(state) !== undefined;
}

interface ReorderOptions {
  getState: () => AppState;
  onReorder: (from: number, to: number) => void;
}

interface Move {
  from: number;
  player: number;
  round: number;
  revision: number;
  loadout: string;
  group: HTMLElement;
  keyboard: boolean;
}

/** Delegated handlers survive normal HUD refreshes; a replaced loadout cancels
 * the gesture instead of applying a stale source index to a different spell. */
export function attachSpellReordering(
  root: HTMLElement | Document,
  { getState, onReorder }: ReorderOptions,
): { cancel: () => void; destroy: () => void } {
  let move: Move | null = null;
  let swallowedKeyup: string | null = null;
  const ownerDocument = root instanceof Document ? root : root.ownerDocument;
  const announcement = ownerDocument.createElement("span");
  announcement.className = "sr-only";
  announcement.dataset.spellReorderStatus = "";
  announcement.setAttribute("role", "status");
  announcement.setAttribute("aria-live", "polite");
  announcement.setAttribute("aria-atomic", "true");
  // The shop is replaced when a request becomes pending; keep its spoken
  // confirmation outside that subtree so it survives the synchronous render.
  ownerDocument.body.append(announcement);

  function slot(target: EventTarget | null): HTMLButtonElement | null {
    if (!(target instanceof Element)) return null;
    const button = target.closest<HTMLButtonElement>("button[data-spell-slot]");
    return button &&
      root.contains(button) &&
      button.closest("[data-spell-reorder]")
      ? button
      : null;
  }

  function slotIndex(button: HTMLButtonElement): number | null {
    const index = Number(button.dataset.spellSlot);
    return Number.isInteger(index) && index >= 0 && index < 4 ? index : null;
  }

  function announce(message: string): void {
    announcement.textContent = message;
  }

  function clear(message = ""): void {
    if (move) {
      move.group.classList.remove("is-reordering");
      for (const button of move.group.querySelectorAll<HTMLButtonElement>(
        "[data-spell-slot]",
      )) {
        if (button.classList.contains("is-dragging"))
          button.setAttribute("aria-pressed", "false");
        button.classList.remove("is-dragging", "is-drop-target");
      }
      announce(message);
    }
    move = null;
  }

  function currentMove(): Move | null {
    if (!move) return null;
    const state = getState();
    const player = editablePlayer(state);
    if (
      !player ||
      !move.group.isConnected ||
      player.id !== move.player ||
      state.snapshot?.round !== move.round ||
      (player.loadout_revision ?? 0) !== move.revision ||
      JSON.stringify(player.loadout) !== move.loadout
    ) {
      clear("Spell slots changed. Pick up the spell again.");
      return null;
    }
    return move;
  }

  function start(button: HTMLButtonElement, keyboard: boolean): boolean {
    clear();
    const state = getState();
    const player = editablePlayer(state);
    const from = slotIndex(button);
    const group = button.closest<HTMLElement>("[data-spell-reorder]")!;
    if (!player || from == null || !player.loadout[from] || button.disabled)
      return false;
    const loadout = JSON.stringify(player.loadout);
    const revision = player.loadout_revision ?? 0;
    if (
      group.dataset.playerId !== String(player.id) ||
      group.dataset.loadoutRevision !== String(revision) ||
      group.dataset.loadout !== loadout
    )
      return false;
    move = {
      from,
      player: player.id,
      round: state.snapshot!.round,
      revision,
      loadout,
      group,
      keyboard,
    };
    group.classList.add("is-reordering");
    button.classList.add("is-dragging");
    button.setAttribute("aria-pressed", "true");
    announce(
      `${SPELL_COPY[player.loadout[from]!].name} picked up. Choose a slot; press Enter or Space to drop, or Escape to cancel.`,
    );
    return true;
  }

  function markTarget(button: HTMLButtonElement): boolean {
    const active = currentMove();
    if (
      !active ||
      button.closest("[data-spell-reorder]") !== active.group ||
      slotIndex(button) == null
    )
      return false;
    for (const target of active.group.querySelectorAll(".is-drop-target"))
      target.classList.remove("is-drop-target");
    if (slotIndex(button) !== active.from)
      button.classList.add("is-drop-target");
    return true;
  }

  function drop(button: HTMLButtonElement): void {
    const active = currentMove();
    const to = slotIndex(button);
    if (
      !active ||
      to == null ||
      button.closest("[data-spell-reorder]") !== active.group
    ) {
      clear();
      return;
    }
    const from = active.from;
    const keyboard = active.keyboard;
    clear(from === to ? "Move canceled." : "Updating spell slots…");
    if (from !== to) onReorder(from, to);
    if (keyboard) {
      // A synchronous renderer may replace the buttons when marking pending.
      const current = root.querySelector<HTMLButtonElement>(
        `[data-spell-reorder] [data-spell-slot="${to}"]`,
      );
      (current ?? button).focus();
    }
  }

  const onDragStart = (event: Event): void => {
    const button = slot(event.target);
    if (!button) return;
    if (!start(button, false)) {
      event.preventDefault();
      return;
    }
    const transfer = (event as DragEvent).dataTransfer;
    if (transfer) {
      transfer.effectAllowed = "move";
      transfer.setData("application/x-warlock-spell-slot", String(move!.from));
    }
    event.stopPropagation();
  };

  const onDragOver = (event: Event): void => {
    const button = slot(event.target);
    if (!button || !move || move.keyboard || !markTarget(button)) return;
    event.preventDefault();
    event.stopPropagation();
    const transfer = (event as DragEvent).dataTransfer;
    if (transfer) transfer.dropEffect = "move";
  };

  const onDragLeave = (event: Event): void => {
    const button = slot(event.target);
    const next = (event as DragEvent).relatedTarget;
    if (button && (!(next instanceof Node) || !button.contains(next)))
      button.classList.remove("is-drop-target");
  };

  const onDrop = (event: Event): void => {
    if (!move || move.keyboard) return;
    event.preventDefault();
    event.stopPropagation();
    const button = slot(event.target);
    if (button) drop(button);
    else clear();
  };

  const onKeyDown = (event: Event): void => {
    const key = event as KeyboardEvent;
    if (key.key === "Escape" && move) {
      key.preventDefault();
      key.stopPropagation();
      const source = move.group.querySelector<HTMLButtonElement>(
        `[data-spell-slot="${move.from}"]`,
      );
      clear("Move canceled.");
      source?.focus();
      return;
    }
    if (key.key === "Tab" && move) clear("Move canceled.");
    const button = slot(key.target);
    if (!button || !editablePlayer(getState())) return;
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(key.key)) {
      const index = slotIndex(button);
      if (index == null) return;
      key.preventDefault();
      key.stopPropagation();
      const next =
        (index + (key.key === "ArrowLeft" || key.key === "ArrowUp" ? 3 : 1)) %
        4;
      const target = button
        .closest("[data-spell-reorder]")!
        .querySelector<HTMLButtonElement>(`[data-spell-slot="${next}"]`);
      target?.focus();
      if (move && target) markTarget(target);
      return;
    }
    if (key.key !== " " && key.key !== "Enter") return;
    if (key.repeat) {
      key.preventDefault();
      return;
    }
    if (move) {
      key.preventDefault();
      key.stopPropagation();
      swallowedKeyup = key.key;
      drop(button);
    } else if (start(button, true)) {
      key.preventDefault();
      key.stopPropagation();
      swallowedKeyup = key.key;
    }
  };

  const onKeyUp = (event: Event): void => {
    const key = event as KeyboardEvent;
    if (key.key === swallowedKeyup) {
      key.preventDefault();
      key.stopPropagation();
      swallowedKeyup = null;
    }
  };

  const onClick = (event: Event): void => {
    const button = slot(event.target);
    if (move?.keyboard && button) {
      event.preventDefault();
      event.stopPropagation();
      drop(button);
    }
  };

  const onFocusOut = (event: Event): void => {
    if (!move?.keyboard) return;
    const next = (event as FocusEvent).relatedTarget;
    if (!(next instanceof Node) || !move.group.contains(next))
      clear("Move canceled.");
  };

  const listeners: [string, EventListener][] = [
    ["dragstart", onDragStart],
    ["dragover", onDragOver],
    ["dragenter", onDragOver],
    ["dragleave", onDragLeave],
    ["drop", onDrop],
    ["dragend", () => clear()],
    ["keydown", onKeyDown],
    ["keyup", onKeyUp],
    ["click", onClick],
    ["focusout", onFocusOut],
  ];
  for (const [event, listener] of listeners)
    root.addEventListener(event, listener, true);
  return {
    cancel: () => clear(),
    destroy: () => {
      clear();
      announcement.remove();
      for (const [event, listener] of listeners)
        root.removeEventListener(event, listener, true);
    },
  };
}
